import { RestApi, RestApiParams } from "./RestApi";
import type { CacheStrategy } from "./CacheApi";
import { ProductOption } from "./StorefrontApi";

export type ShopifyApiParams = Omit<RestApiParams, "name">;

export class ShopifyApi extends RestApi {
  pendingCart?: Promise<ShopifyCart>;

  constructor(params: ShopifyApiParams) {
    super({
      ...params,
      name: "shopify",
    });
  }

  loadCart(params?: { cacheStrategy?: CacheStrategy }) {
    if (this.pendingCart) {
      return this.pendingCart;
    }

    const promise = this.get<ShopifyCart>({
      path: "/cart.js",
      cacheStrategy: params?.cacheStrategy,
    }).then((result) => {
      this.pendingCart = undefined;
      return result;
    });

    this.pendingCart = promise;

    return promise;
  }

  addToCart(body: AddToCartBody) {
    return this.post<AddToCartResponse>({
      path: "/cart/add.js",
      body,
    });
  }
}

export type AddToCartResponse =
  | {
      items: ShopifyCartItem[];
    }
  | ShopifyCartItem;

export interface ShopifyProduct {
  id: number;
  available: boolean;
  compare_at_price_max: number;
  compare_at_price_min: number;
  compare_at_price_varies: boolean;
  featured_image: string | null;
  handle: string;
  images: string[];
  options: ProductOption[];
  price_max: number;
  price_min: number;
  price_varies: boolean;
  tags: string[];
  title: string;
  type: string;
  url: string;
  variants: ShopifyProductVariant[];
  vendor: string;
  metafields?: Record<string, Record<string, string>>;
}

export interface ShopifyProductMedia {
  id: number;
  alt: null | string;
  aspect_ratio: number;
  height: number;
  media_type: string;
  position: number;
  preview_image: ShopifyImage;
  src: string;
  width: number;
}

export interface ShopifyImage {
  aspect_ratio: number;
  height: number;
  src: string;
  width: number;
}

// export interface ShopifyProductOption {
//   name: string;
//   values: string[];
// }

export interface ShopifyProductVariant {
  id: number;
  available: boolean;
  compare_at_price: null | number;
  featured_image: null | ShopifyProductVariantImage;
  // inventory_management: null | "shopify";
  // inventory_policy: "continue" | "deny";
  // inventory_quantity: number;
  option1: string;
  option2: null | string;
  option3: null | string;
  // options: string[];
  price: number;
  // requires_shipping: boolean;
  sku: string;
  title: string;
}

export interface ShopifyProductVariantImage {
  alt: null | string;
  src: string;
}

export type ShopifyProductOptionKey = "option1" | "option2" | "option3";

export interface ShopifyCartItem {
  discounted_price: number;
  featured_image: ShopifyProductMedia;
  final_line_price: number;
  final_price: number;
  gift_card: false;
  grams: number;
  handle: string;
  id: number;
  image: string;
  key: string;
  line_level_total_discount: number;
  line_price: number;
  options_with_values: { name: string; value: string }[];
  original_line_price: number;
  original_price: number;
  price: number;
  product_description: string;
  product_has_only_default_variant: false;
  product_id: number;
  product_title: string;
  product_type: string;
  properties: null;
  quantity: number;
  requires_shipping: true;
  sku: string;
  taxable: true;
  title: string;
  total_discount: number;
  url: string;
  variant_id: number;
  variant_options: string[];
  variant_title: string;
  vendor: string;
}

export interface ShopifyCart {
  token: string;
  total_price: number;
  total_weight: number;
  item_count: number;
  items: ShopifyCartItem[];
  requires_shipping: boolean;
  currency: string;
  items_subtotal_price: number;
}

export type AddToCartBody =
  | {
      sections?: string[];
      sections_url?: string;
      items: {
        id: number;
        quantity: number;
        properties?: Record<string, string>;
      }[];
    }
  | {
      sections?: string[];
      sections_url?: string;
      id: number;
      quantity: number;
      properties?: Record<string, string>;
    };
